import { memo } from 'react';
import common from '../styles/common.module.css';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';

const HOTKEYS = [
  ['Space/Enter', 'Attack'],
  ['T', 'Reset target'],
  ['R', 'Reload weapon'],
  ['C', 'Clear log'],
  ['N', 'New tab'],
  ['D', 'Duplicate tab'],
  ['F', 'Open the action drum'],
  ['E', 'End the turn'],
  ['Tab/Shift+Tab', 'Next / previous row of the order rail'],
  ['1-9', 'Select slot'],
  ['Dbl-click slot', 'Edit'],
  ['Dbl-click tab', 'Rename'],
  ['Right-click tab', 'Menu'],
  ['` or /', 'Talk (the vox composer; / starts a command)'],
  ['F1', 'Hotkeys'],
  ['Escape', 'Close'],
];

/**
 * THE MAP'S GESTURES, per mode.
 *
 * Raised by the user: "How are users supposed to know all keyboard +
 * mouse combinations?" A fair question - Deploy alone grew from one
 * gesture to eight in a week, and the mode hint line can honestly hold
 * about three (v8.25.0).
 *
 * This list is the reference; the bar teaches at the moment of use. The
 * modal already carried mouse entries, so it is the obvious home rather
 * than a new idea.
 */
const MAP_GESTURES = [
  [
    'Play',
    [
      ['Drag', 'Measure'],
      ['Click a marker, then hold', 'Move them'],
      ['Click a marker', 'Target them'],
      ['Right-drag', 'Turn the camera'],
      ['Right-hold', 'Ping'],
      ['Wheel', 'Zoom'],
    ],
  ],
  [
    'Build',
    [
      ['Drag', 'Place'],
      ['Right-drag', 'Erase'],
      ['Shift + right-drag', 'Turn the camera'],
    ],
  ],
  [
    'Deploy',
    [
      ['Click', 'Plant the marker'],
      ['Double-click', 'Plant, and ring the active room'],
      ['Drag', 'A formation line'],
      ['Alt + drag', 'Fill that area with a block'],
      ['Drag a name in', 'Place that one there'],
      ['Right-click a marker', 'Recall them to the room'],
      ['Right-drag', 'Sweep - recall everyone inside'],
      ['Shift + right-drag', 'Turn the camera'],
    ],
  ],
];

function HotkeysModal({ show, onClose }) {
  const trapRef = useFocusTrap(show);
  const backdrop = useModalBackdrop(onClose, trapRef);
  if (!show) return null;
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        className={common.modalContent}
        style={{ maxWidth: 460, maxHeight: '80vh', overflowY: 'auto' }}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
      >
        <h3 style={{ color: 'var(--accent)', fontFamily: 'Cinzel, serif', margin: '0 0 12px' }}>Hotkeys</h3>
        <div style={{ fontSize: 12, lineHeight: 2 }}>
          {HOTKEYS.map(([k, d]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <code style={{ background: 'var(--border-subtle)', padding: '1px 6px', borderRadius: 3 }}>{k}</code>
              <span style={{ color: 'var(--text-muted)' }}>{d}</span>
            </div>
          ))}
        </div>
        {MAP_GESTURES.map(([mode, rows]) => (
          <div key={mode} data-testid={`gestures-${mode.toLowerCase()}`}>
            <h4 style={{ color: 'var(--accent)', fontFamily: 'Cinzel, serif', margin: '14px 0 6px', fontSize: 13 }}>
              The map - {mode}
            </h4>
            <div style={{ fontSize: 12, lineHeight: 2 }}>
              {rows.map(([k, d]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <code
                    style={{
                      background: 'var(--border-subtle)',
                      padding: '1px 6px',
                      borderRadius: 3,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {k}
                  </code>
                  <span style={{ color: 'var(--text-muted)', textAlign: 'right' }}>{d}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default memo(HotkeysModal);
